import { createServerFn } from "@tanstack/react-start";
import { getCookie, setCookie } from "@tanstack/react-start/server";
import { type } from "arktype";
import { createContext, useContext, useState } from "react";
import type { QuickFilter } from "~/hooks/user-chart-filters";
import { Route } from "~/routes/__root";

// Represents the user preferences that can be stored in cookies
const PreferencesSchema = type({
	// How to represent channels in the channels overview, either as a grid or a list
	channelsListView: type("unknown")
		.pipe((value) =>
			typeof value === "string" && ["grid", "list"].includes(value) ? value : "list",
		)
		.default("list"),
	// Playback rate for the episode player, must be a number between 0.25 and 4
	playbackRate: type("unknown")
		.pipe((value) => (typeof value === "number" && value > 0 && value < 10 ? value : 1))
		.default(1),
	// Quick filter for stats, must be one of the predefined values
	statsQuickFilter: type("unknown")
		.pipe((value) =>
			typeof value === "string" &&
			(
				[
					"all",
					"today",
					"7d",
					"30d",
					"365d",
					"m",
					"y",
					"-m",
					"-y",
					"custom",
				] satisfies Array<QuickFilter>
			).includes(value)
				? value
				: "7d",
		)
		.default("7d"),
}).onUndeclaredKey("delete");

type Preferences = typeof PreferencesSchema.infer;

const preferencesCookieName = "dashboard-preferences";

type PreferencesProviderProps = {
	children: React.ReactNode;
};

type PreferencesProviderState = {
	preferences: Preferences;
	setPreferences: (preferences: Partial<Preferences>) => void;
};

const initialState: PreferencesProviderState = {
	preferences: PreferencesSchema.assert({}),
	setPreferences: () => null,
};

const PreferencesProviderContext = createContext<PreferencesProviderState>(initialState);

/**
 * Server function to get the current preferences from cookies.
 * Returns the preferences object or a default value if not set.
 */
export const getPreferencesServerFn = createServerFn().handler((): Preferences => {
	const cookiePreferences = getCookie(preferencesCookieName);

	const preferences = PreferencesSchema(cookiePreferences ? JSON.parse(cookiePreferences) : {});

	if (preferences instanceof type.errors) {
		return PreferencesSchema.assert({});
	}

	return preferences;
});

export const setPreferencesServerFn = createServerFn({ method: "POST" })
	.inputValidator(PreferencesSchema)
	.handler(async ({ data }) => {
		return setCookie(preferencesCookieName, JSON.stringify(PreferencesSchema.assert(data)));
	});

export function PreferencesProvider({ children, ...props }: PreferencesProviderProps) {
	const { preferences: initialPreferences } = Route.useLoaderData();

	// Use local state to manage preferences on the client side
	const [preferences, setPreferencesState] = useState<Preferences>(initialPreferences);

	function setPreferences(newPreferences: Partial<Preferences>) {
		setPreferencesState((currentPreferences) => ({
			...currentPreferences,
			...newPreferences,
		}));

		setPreferencesServerFn({
			data: { ...preferences, ...newPreferences },
		});
	}

	const value = {
		preferences,
		setPreferences,
	};

	return (
		<PreferencesProviderContext.Provider {...props} value={value}>
			{children}
		</PreferencesProviderContext.Provider>
	);
}

export const usePreferences = () => {
	const context = useContext(PreferencesProviderContext);

	if (context === undefined)
		throw new Error("usePreferences must be used within a PreferencesProvider");

	return context;
};
